import { FC } from 'react'
import Error from 'next/error'
import { GetServerSideProps } from 'next'

import { storyblokInstance } from '@app/services/storyblok-service'
import { StoryblokResult } from '@storyblok/react'
import { useEditor } from '@app/hooks/use-editor'
import { LayoutArticle } from '@app/components/layout-article'
import { Bridge } from '@app/components/bridge'
import { countryCodeToLocale } from '@app/utils/i18n'
import { articleService } from '@app/services/article-service'
import { linkService, LinkMap } from '@app/services/link-service'
import { Article } from '@app/components/article'
import { getRequestIdFromHeader, log } from '@app/lib/log'
import {
  getLayoutModifyingQueryParams,
  supportedQueryParameters,
} from '@app/utils/query-parameters-utils'
import {
  generateHrefLangData,
  getAlternates,
} from '@app/services/href-lang-service'
import { Routes } from '@app/utils/routes'
import { OttoIconProvider } from '@izettle/public-site-components/dist/help-center'
import { getOttoIcons } from '@app/utils/get-otto-icons'
import { OttoIconCollection } from '@izettle/public-site-components/dist/components/contexts/otto-icon'

type Props = {
  page: StoryblokResult | null
  layout: StoryblokResult | null
  locale: string | null
  linkMap: LinkMap | null
  error?: { statusCode: number }
  queryParameters: supportedQueryParameters
  ottoIcons?: OttoIconCollection | null
}

const ArticleWrapper: FC<Props> = ({
  error,
  page,
  layout,
  queryParameters,
  ottoIcons,
}) => {
  if (error) {
    return <Error statusCode={error.statusCode} />
  }

  if (!page) {
    return null
  }

  const articleContent = useEditor(page.data.story.content)
  const layoutContent = layout ? layout.data.story.content : {}

  return (
    <Bridge>
      <OttoIconProvider ottoIcons={ottoIcons as OttoIconCollection}>
        <LayoutArticle
          {...layoutContent}
          queryParameters={queryParameters}
          title={articleContent.title}
          path={page.data.story.uuid}
          articleId={page?.data?.story?.content?.articleId?.id || ''}
          fullSlug={page?.data?.story?.full_slug || ''}
          badgeGroup={articleContent.badgeGroup}
        >
          <Article {...articleContent} />
        </LayoutArticle>
      </OttoIconProvider>
    </Bridge>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  res,
}) => {
  await generateHrefLangData()
  storyblokInstance.setQuery(query)

  const { country = 'gb', slug = '' } = query
  const queryParameters = getLayoutModifyingQueryParams(query)
  const path = Array.isArray(slug) ? slug.join('/') : slug
  const countryCode = Array.isArray(country) ? country[0] : country
  const locale = countryCodeToLocale(countryCode)

  const articleId = path.split('-')[0]
  const articleUuid = articleService.getArticleUuid(articleId)

  let page = null
  let layout = null
  const hrefLangAlternates = getAlternates(
    `/${countryCode}${Routes.HELP}${Routes.ARTICLES}/${slug}`
  )

  try {
    page = await storyblokInstance.get(`cdn/stories/${articleUuid}`, {
      resolve_relations: 'global-reference.reference',
      find_by: 'uuid',
      language: locale,
    })

    const layoutUuid = page?.data?.story?.content?.layout
    if (layoutUuid) {
      layout = await storyblokInstance.getLayout(locale, layoutUuid)
    }

    const { uuid: pageUuid, published_at: pageTimestamp } = page.data.story
    const layoutTimestamp = layout?.data?.story?.published_at

    const ottoIcons: OttoIconCollection = await getOttoIcons([
      {
        uuid: pageUuid,
        timestamp: pageTimestamp,
        content: page.data.story.content,
      },
      {
        uuid: layoutUuid,
        timestamp: layoutTimestamp,
        content: layout?.data.story.content,
      },
    ])

    log.debug('help-center - articles/[...slug].tsx', {
      reqId: getRequestIdFromHeader(res),
      layoutUuid,
      articleUuid,
      locale,
      hrefLangAlternates,
      statusCode: res.statusCode,
    })

    return {
      props: {
        page,
        layout,
        queryParameters,
        linkMap: linkService.linkMap,
        hrefLangAlternates,
        locale,
        ottoIcons,
      },
    }
  } catch (e) {
    res.statusCode = 404

    log.error('help-center - articles/[...slug].tsx', {
      reqId: getRequestIdFromHeader(res),
      articleUuid,
      locale,
      statusCode: res.statusCode,
      error: { statusCode: (e as any)?.response?.status },
    })

    return {
      props: {
        page,
        layout,
        queryParameters,
        linkMap: linkService.linkMap,
        locale,
        error: { statusCode: (e as any)?.response?.status },
      },
    }
  }
}

export default ArticleWrapper
